import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Layers, Code, BarChart3, Zap, Shield, Users } from 'lucide-react';
import SEO from '../components/SEO';

export default function AEMDevelopment() {
  const canonical = 'https://divaisetech.com/digital-experience-platform-solutions';
  const servicePageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'AEM Development Services',
    description: 'Enterprise Adobe Experience Manager consulting, implementation, migration, and optimization services.',
    url: canonical,
    provider: {
      '@type': 'Organization',
      name: 'DivaiseTech',
      url: 'https://divaisetech.com',
      logo: 'https://divaisetech.com/logo.png',
    },
    areaServed: {
      '@type': 'Place',
      name: 'Worldwide',
    },
    serviceType: 'Adobe Experience Manager Services',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
    },
  };

  const aemSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'AEM Development Services | Adobe Experience Manager Implementation',
      description: 'Enterprise Adobe Experience Manager (AEM) consulting, development, migration, and optimization services for global brands.',
      provider: {
        '@type': 'Organization',
        name: 'Divaise Technology Private Limited',
        url: 'https://divaisetech.com',
      },
      serviceType: 'AEM Development & Adobe Experience Cloud Solutions',
      areaServed: {
        '@type': 'Place',
        name: 'Worldwide',
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'AEM Services',
        itemListElement: [
          { '@type': 'Service', name: 'AEM Sites Development' },
          { '@type': 'Service', name: 'AEM Cloud Upgrade & Migration' },
          { '@type': 'Service', name: 'AEM Assets (DAM) Setup' },
        ],
      },
    },
  ];

  const capabilities = [
    {
      icon: Code,
      title: 'AEM Sites Development',
      description: 'Full-stack AEM Sites architecture, component development, and site optimization.',
      details: [
        'Custom component design and development',
        'Template-driven page authoring',
        'Advanced personalization & targeting',
        'Multi-site management',
        'Performance tuning & optimization',
      ],
    },
    {
      icon: Zap,
      title: 'AEM Cloud Migration',
      description: 'Seamless migration from AEM on-premises to AEM as a Cloud Service.',
      details: [
        'Cloud readiness assessment',
        'Code refactoring & modernization',
        'Zero-downtime migration planning',
        'Content & asset migration',
        'Post-migration optimization',
      ],
    },
    {
      icon: Layers,
      title: 'AEM Assets & DAM',
      description: 'Digital Asset Management setup, governance, and workflow automation.',
      details: [
        'DAM architecture design',
        'Metadata framework & taxonomy',
        'Automated asset workflows',
        'Brand asset governance',
        'Integration with publishing platforms',
      ],
    },
    {
      icon: BarChart3,
      title: 'AEM Analytics & Performance',
      description: 'Content performance monitoring and optimization using Adobe Analytics & Launch.',
      details: [
        'Analytics integration setup',
        'Adobe Launch tag management',
        'Conversion tracking & analysis',
        'A/B testing & personalization',
        'Real-time performance dashboards',
      ],
    },
  ];

  const faqs = [
    {
      question: 'What is the typical timeline for AEM implementation?',
      answer: 'A standard AEM implementation takes 4-6 months depending on complexity, scope, and your team\'s readiness. Discovery (2-4 weeks), development (8-12 weeks), testing (2-4 weeks), and launch preparation (1-2 weeks).',
    },
    {
      question: 'Should we migrate to AEM Cloud or stay on-premises?',
      answer: 'AEM Cloud is recommended for most enterprises due to lower operational overhead, automatic scaling, built-in security, and simplified maintenance. On-premises may be suitable only if you have strict data residency or legacy system constraints.',
    },
    {
      question: 'How do you ensure content authoring productivity?',
      answer: 'We design intuitive templates, create custom components, implement workflow automation, and provide comprehensive author training. A well-designed authoring experience reduces time-to-publish by 30-50%.',
    },
    {
      question: 'What is your approach to AEM performance optimization?',
      answer: 'We optimize at every layer: code efficiency (OSGi bundles, HTL best practices), caching strategies (Dispatcher, CDN), content delivery, and infrastructure tuning. Average page load improvements are 40-60%.',
    },
    {
      question: 'Do you provide post-launch support and training?',
      answer: 'Yes. We offer 6-12 months of hypercare support, author training workshops, system administrator training, and ongoing optimization consulting.',
    },
  ];

  return (
    <div>
      <SEO
        title="AEM Development Services | Adobe Experience Manager Implementation"
        description="Enterprise Adobe Experience Manager (AEM) consulting, development, migration, and optimization. AEM Sites, Cloud migration, Assets (DAM), and performance tuning."
        keywords="AEM development, Adobe Experience Manager, AEM implementation, AEM migration, AEM consultants, AEM Cloud, Digital experience platform"
        canonical={canonical}
        schema={[...aemSchema, servicePageSchema]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 -left-40 w-80 h-80 bg-accent rounded-full mix-blend-multiply filter blur-3xl"></div>
          <div className="absolute top-40 -right-40 w-80 h-80 bg-secondary rounded-full mix-blend-multiply filter blur-3xl"></div>
        </div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="mb-12">
            <span className="inline-block bg-accent/20 text-accent px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Adobe Experience Cloud Solutions
            </span>
            <h1 className="text-5xl md:text-6xl font-bold mb-8 leading-tight">
              Enterprise AEM Development & Consulting Services
            </h1>
            <p className="text-xl text-slate-200 max-w-3xl mb-8 leading-relaxed">
              Build world-class digital experiences on Adobe Experience Manager. From AEM Sites architecture to Cloud migration and DAM implementation, we deliver enterprise-grade solutions that drive customer engagement and business growth.
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-16">
            <Link
              to="/free-aem-consultation"
              className="inline-flex items-center bg-accent text-black px-8 py-4 rounded-lg font-semibold hover:bg-accent/90 transition-all hover:shadow-lg"
            >
              Get Free AEM Consultation <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center border-2 border-secondary text-white px-8 py-4 rounded-lg font-semibold hover:bg-secondary/20 transition-all"
            >
              Schedule Expert Review
            </Link>
          </div>

          {/* Key Stats */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-lg p-6">
              <div className="text-3xl font-bold text-accent mb-2">25+</div>
              <p className="text-slate-200">AEM Implementations Delivered</p>
            </div>
            <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-lg p-6">
              <div className="text-3xl font-bold text-accent mb-2">500M+</div>
              <p className="text-slate-200">Monthly Page Views Optimized</p>
            </div>
            <div className="bg-black/40 backdrop-blur-sm border border-white/10 rounded-lg p-6">
              <div className="text-3xl font-bold text-accent mb-2">40%+</div>
              <p className="text-slate-200">Avg Content Production Improvement</p>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-8 text-white">About AEM Development Services</h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                Adobe Experience Manager (AEM) is the enterprise standard for digital experience management. Built on a headless-flexible architecture, AEM powers millions of customer touchpoints for global brands in retail, financial services, media, and software.
              </p>
              <p className="text-lg text-slate-300 mb-6 leading-relaxed">
                Our AEM consulting team specializes in helping enterprises architect, implement, migrate, and optimize their digital experience platform. Whether you're implementing AEM for the first time, upgrading from AEM 6.x to Cloud, or optimizing performance, we deliver measurable results.
              </p>
            </div>
            <div className="bg-slate-950 rounded-xl border border-white/10 p-8">
              <h3 className="text-xl font-bold mb-6 text-white">We Help You With:</h3>
              <ul className="space-y-3">
                {['AEM Sites new implementations', 'AEM on-premises to Cloud migration', 'AEM Assets and DAM setup', 'Performance optimization & tuning', 'Custom component development', 'Author training & enablement'].map((item, idx) => (
                  <li key={idx} className="flex gap-3 items-start">
                    <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-4 text-center text-white">Key AEM Capabilities</h2>
          <p className="text-xl text-slate-300 text-center mb-16 max-w-2xl mx-auto">
            End-to-end AEM services covering every stage of your digital experience journey.
          </p>
          <div className="grid md:grid-cols-2 gap-8">
            {capabilities.map((cap, idx) => {
              const Icon = cap.icon;
              return (
                <div key={idx} className="bg-slate-950 border border-white/10 rounded-xl p-8 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <Icon className="w-8 h-8 text-accent flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-3 text-white">{cap.title}</h3>
                      <p className="text-slate-300 mb-4">{cap.description}</p>
                      <ul className="space-y-2">
                        {cap.details.map((detail, didx) => (
                          <li key={didx} className="text-sm text-slate-300 flex gap-2">
                            <span className="text-accent font-bold">•</span> {detail}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-16 text-center text-white">AEM & Adobe Cloud Platform Stack</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-950 rounded-xl border border-white/10 p-8">
              <h3 className="text-xl font-bold mb-6 text-white">AEM Sites</h3>
              <ul className="space-y-2 text-slate-300">
                <li>• AEM as a Cloud Service (AEMaaCS)</li>
                <li>• AEM 6.5 / 6.4 / 6.3</li>
                <li>• HTL Templating</li>
                <li>• Component Development (React, Vue)</li>
                <li>• Sling Models & Servlets</li>
                <li>• OSGi Bundles</li>
              </ul>
            </div>
            <div className="bg-slate-950 rounded-xl border border-white/10 p-8">
              <h3 className="text-xl font-bold mb-6 text-white">Adobe Cloud Services</h3>
              <ul className="space-y-2 text-slate-300">
                <li>• Adobe Commerce Integration</li>
                <li>• Adobe Analytics Integration</li>
                <li>• Adobe Launch Tag Management</li>
                <li>• Adobe Target Personalization</li>
                <li>• Real-time CDP Integration</li>
                <li>• Adobe Cloud APIs</li>
              </ul>
            </div>
            <div className="bg-slate-950 rounded-xl border border-white/10 p-8">
              <h3 className="text-xl font-bold mb-6 text-white">Infrastructure & DevOps</h3>
              <ul className="space-y-2 text-slate-300">
                <li>• Cloud Manager (CI/CD)</li>
                <li>• Dispatcher Config & Optimization</li>
                <li>• AWS/Azure/GCP Hosting</li>
                <li>• CDN Integration</li>
                <li>• Monitoring & Analytics</li>
                <li>• Docker & Container Orchestration</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-16 text-center text-white">Enterprise Benefits</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Faster Content Deployment',
                desc: 'Structured publishing workflow and templates reduce time-to-publish by 40-50%.',
              },
              {
                title: 'Improved Digital Experiences',
                desc: 'Personalization, A/B testing, and analytics-driven optimization boost engagement.',
              },
              {
                title: 'Reduced Operational Costs',
                desc: 'Cloud Service model eliminates infrastructure overhead and reduces admin burden.',
              },
              {
                title: 'Lightning-Fast Performance',
                desc: 'Optimized caching, CDN, and infrastructure tuning maintain sub-2s page loads.',
              },
              {
                title: 'Enterprise Security & Compliance',
                desc: 'Built-in security controls, audit trails, and governance frameworks.',
              },
              {
                title: 'Scalability Without Limits',
                desc: 'Auto-scaling infrastructure handles traffic spikes during campaigns.',
              },
            ].map((benefit, idx) => (
              <div key={idx} className="bg-slate-950 rounded-lg p-8 border border-white/10">
                <h3 className="text-lg font-bold mb-2 text-white">{benefit.title}</h3>
                <p className="text-slate-300">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-16 text-center text-white">Industries We Serve</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {['Retail & E-commerce', 'Financial Services', 'Healthcare & Life Sciences', 'Media & Publishing', 'Software & SaaS', 'Telecommunications', 'Manufacturing', 'Higher Education'].map((industry, idx) => (
              <div key={idx} className="bg-slate-950 rounded-lg border border-white/10 p-6 text-center hover:shadow-lg transition-shadow">
                <p className="font-semibold text-white">{industry}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-16 text-center text-white">Why Choose Divaise for AEM?</h2>
          <div className="grid md:grid-cols-2 gap-12">
            {[
              {
                icon: Users,
                title: 'Adobe Certified Experts',
                desc: 'Our team includes Adobe AEM developers, architects, and solution consultants with 50+ years combined AEM experience.',
              },
              {
                icon: BarChart3,
                title: 'Results-Driven Approach',
                desc: 'We measure success with KPIs: page speed, content productivity, conversion rates, and cost savings.',
              },
              {
                icon: Shield,
                title: 'Enterprise-Grade Security',
                desc: 'All implementations follow enterprise security standards and OWASP best practices.',
              },
              {
                icon: Zap,
                title: 'Fast Implementation',
                desc: 'Our proven AEM delivery framework cuts implementation time by 20-30% vs. industry average.',
              },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="flex gap-6">
                  <Icon className="w-8 h-8 text-accent flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold mb-2 text-white">{item.title}</h3>
                    <p className="text-slate-300">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Implementation Approach Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-16 text-center text-white">Our AEM Implementation Approach</h2>
          <div className="space-y-6">
            {[
              {
                phase: '1. Discovery & Assessment',
                details: 'Business requirements, technical audit, roadmap planning, team kickoff',
              },
              {
                phase: '2. Architecture & Design',
                details: 'System architecture, component design, content model, integrations planning',
              },
              {
                phase: '3. Development & Testing',
                details: 'Custom component development, automated testing, performance optimization',
              },
              {
                phase: '4. Content Migration',
                details: 'Legacy content assessment, ETL pipeline, data validation, import',
              },
              {
                phase: '5. Training & Go-Live',
                details: 'Author training, admin training, UAT support, production deployment',
              },
              {
                phase: '6. Post-Launch Support',
                details: 'Hypercare support, optimization, training, handover documentation',
              },
            ].map((item, idx) => (
              <div key={idx} className="bg-slate-950 border border-white/10 rounded-lg p-8 flex gap-6">
                <div className="bg-accent text-black rounded-full w-12 h-12 flex items-center justify-center flex-shrink-0 font-bold">
                  {item.phase.split('.')[0]}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-2">{item.phase}</h3>
                  <p className="text-slate-300">{item.details}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold mb-16 text-center text-white">AEM Success Story</h2>
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-xl p-12">
            <h3 className="text-2xl font-bold mb-4">Multi-Brand AEM Cloud Migration: Global Fashion Retailer</h3>
            <p className="text-slate-200 mb-6 leading-relaxed">
              A $2B fashion retailer with 15 regional websites on legacy AEM 6.3 needed to modernize to AEM Cloud. The challenge: migrate 50,000+ products and assets with zero downtime across European, Asian, and US markets.
            </p>
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="text-4xl font-bold text-accent mb-2">8 weeks</div>
                <p className="text-slate-200">End-to-end migration timeline</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-accent mb-2">0 downtime</div>
                <p className="text-slate-200">Achieved during cutover</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-accent mb-2">35% faster</div>
                <p className="text-slate-200">Page load times post-optimization</p>
              </div>
            </div>
            <Link to="/contact" className="inline-flex items-center bg-accent text-black px-6 py-3 rounded-lg font-semibold hover:bg-accent/90 transition-all">
              Learn How We Can Help Your Project <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold mb-16 text-center text-white">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <details key={idx} className="group bg-slate-950 border border-white/10 rounded-lg p-6 cursor-pointer hover:shadow-lg transition-shadow">
                <summary className="flex justify-between items-center font-semibold text-white">
                  {faq.question}
                  <span className="text-accent">+</span>
                </summary>
                <p className="mt-4 text-slate-300 leading-relaxed">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-r from-accent to-accent/80 text-black">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Transform Your Digital Experience?</h2>
          <p className="text-xl mb-8 text-black/80">
            Connect with our AEM experts to discuss your project, timeline, and budget.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/free-aem-consultation"
              className="inline-flex items-center bg-black text-white px-8 py-4 rounded-lg font-semibold hover:bg-black/90 transition-all"
            >
              Get Free AEM Consultation <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center border-2 border-black bg-transparent text-black px-8 py-4 rounded-lg font-semibold hover:bg-black/10 transition-all"
            >
              Contact Our Team
            </Link>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-16 px-6 bg-black border-t border-gray-800">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-lg font-bold text-white mb-6">Related Services</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/solutions" className="text-accent hover:underline font-semibold">
              View All Solutions →
            </Link>
            <Link to="/it-hardware-solutions" className="text-accent hover:underline font-semibold">
              Enterprise Hardware Solutions →
            </Link>
            <Link to="/about" className="text-accent hover:underline font-semibold">
              Learn About Our Team →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
